import { useState, useEffect, useCallback, useMemo } from "react";
import PMWorkOrderReview from "./PMWorkOrderReview";

/**
 * PM Work Order Review Page
 *
 * Two modes:
 * 1. Demo mode - Uses sample data for training/demonstration
 * 2. Live mode - Fetches real pending work orders from API with JobBoss data
 */

// Sample data for demo mode
const SAMPLE_INTAKES = [
  {
    id: "demo-1",
    email_subject: "Kickoff: 10584 Nut Thins Mock Ups",
    email_from: "Susan.Arnold@3dcolor.com",
    extractedData: {
      customer_name: "Blue Diamond",
      project_name: "Nut Thins Mock Ups",
      quote_number: "10584",
      ship_date: "2023-09-17",
      is_rush: false,
      contact_name: "Susan Arnold",
      confidence: 0.92,
      skus: [
        {
          sku_name: "Nut Thins Original",
          quantity: 2,
          needs_model: true,
          needs_paint: false,
          needs_graphics: false,
          needs_opti: true,
          jobBossMatch: true,
        },
        {
          sku_name: "Nut Thins Honey",
          quantity: 2,
          needs_model: true,
          needs_paint: false,
          needs_graphics: false,
          needs_opti: true,
          jobBossMatch: true,
        },
        {
          sku_name: "Nut Thins Cheddar",
          quantity: 2,
          needs_model: true,
          needs_paint: false,
          needs_graphics: false,
          needs_opti: true,
          jobBossMatch: true,
        },
      ],
    },
  },
  {
    id: "demo-2",
    email_subject: "Kickoff: 10563 Swiffer Side-Kick Mock Ups",
    email_from: "Susan.Arnold@3dcolor.com",
    extractedData: {
      customer_name: "Procter & Gamble",
      project_name: "Swiffer Side-Kick Mock Ups",
      quote_number: "10563",
      ship_date: null,
      is_rush: false,
      contact_name: "Susan Arnold",
      confidence: 0.72,
      skus: [
        {
          sku_name: "Swiffer WetJet Sidekick",
          quantity: 3,
          needs_model: true,
          needs_paint: true,
          needs_graphics: true,
          needs_opti: true,
          jobBossMatch: false,
        },
        {
          sku_name: "Swiffer Duster Sidekick",
          quantity: 3,
          needs_model: true,
          needs_paint: true,
          needs_graphics: true,
          needs_opti: false,
          jobBossMatch: false,
        },
      ],
    },
  },
  {
    id: "demo-3",
    email_subject: "Kickoff 10381 Tampax WMT + HEB Rush Cartons",
    email_from: "Susan.Arnold@3dcolor.com",
    extractedData: {
      customer_name: null,
      project_name: "Tampax WMT + HEB Rush Cartons",
      quote_number: "10381",
      ship_date: null,
      is_rush: true,
      contact_name: null,
      confidence: 0.44,
      skus: [],
    },
  },
];

export default function PMWorkOrderDemo({ focusIntakeId = null }) {
  // If focusIntakeId is provided, start in live mode
  const [mode, setMode] = useState(focusIntakeId ? "live" : "demo");
  const [intakes, setIntakes] = useState(SAMPLE_INTAKES);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [approvedOrders, setApprovedOrders] = useState([]);
  const [skippedOrders, setSkippedOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [jobBossData, setJobBossData] = useState(null);
  const [loadingJobBoss, setLoadingJobBoss] = useState(false);

  const currentIntake = intakes[currentIndex];

  // Fetch pending work orders from API
  const fetchPendingWorkOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = sessionStorage.getItem("msal.backendToken");
      const res = await fetch("/api/pm-center/work-orders/pending", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch pending work orders");
      }

      const data = await res.json();

      if (data.intakes && data.intakes.length > 0) {
        setIntakes(data.intakes);

        // If focusIntakeId is provided, find and focus on that intake
        if (focusIntakeId) {
          const focusIndex = data.intakes.findIndex(
            (intake) => intake.id === focusIntakeId || intake.id === parseInt(focusIntakeId, 10)
          );
          setCurrentIndex(focusIndex >= 0 ? focusIndex : 0);
        } else {
          setCurrentIndex(0);
        }
      } else {
        setIntakes([]);
        setError("No pending work orders to review");
      }
    } catch (err) {
      console.error("Error fetching work orders:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [focusIntakeId]);

  // Fetch JobBoss quote line items for current intake
  const fetchJobBossData = useCallback(async (quoteNumber) => {
    if (!quoteNumber) {
      setJobBossData(null);
      return;
    }

    setLoadingJobBoss(true);
    try {
      const token = sessionStorage.getItem("msal.backendToken");
      const res = await fetch(`/api/pm-center/quotes/${quoteNumber}/line-items`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setJobBossData(data);
        console.log(`📦 Loaded ${data.count} line items from JobBoss for quote ${quoteNumber}`);
      } else {
        const errorText = await res.text();
        console.error(`❌ JobBoss fetch failed: ${res.status} ${res.statusText}`, errorText);
        setJobBossData(null);
      }
    } catch (err) {
      console.error("Error fetching JobBoss data:", err);
      setJobBossData(null);
    } finally {
      setLoadingJobBoss(false);
    }
  }, []);

  // Switch between demo and live mode
  useEffect(() => {
    if (mode === "live") {
      fetchPendingWorkOrders();
    } else {
      setIntakes(SAMPLE_INTAKES);
      setCurrentIndex(0);
      setError(null);
    }
    setApprovedOrders([]);
    setSkippedOrders([]);
    setJobBossData(null);
  }, [mode, fetchPendingWorkOrders]);

  // Fetch JobBoss data when current intake changes
  useEffect(() => {
    if (currentIntake?.extractedData?.quote_number) {
      fetchJobBossData(currentIntake.extractedData.quote_number);
    } else {
      setJobBossData(null);
    }
  }, [currentIntake, fetchJobBossData]);

  // Memoized merge of AI extraction with JobBoss data
  // This creates a stable reference that only changes when actual data changes
  const mergedData = useMemo(() => {
    if (!currentIntake) return null;

    const extracted = currentIntake.extractedData || {};
    let mergedSkus = extracted.skus || [];

    // If we have JobBoss line items, use them as source of truth
    if (jobBossData?.lineItems?.length > 0) {
      mergedSkus = jobBossData.lineItems.map((lineItem) => {
        // Try to find matching AI extraction
        const aiMatch = (extracted.skus || []).find(
          (sku) =>
            sku.sku_name?.toLowerCase().includes(lineItem.description?.toLowerCase()?.substring(0, 15)) ||
            lineItem.description?.toLowerCase().includes(sku.sku_name?.toLowerCase()?.substring(0, 15))
        );

        return {
          sku_name: lineItem.description || lineItem.partNumber,
          part_number: lineItem.partNumber,
          quantity: lineItem.quantity1 || lineItem.quantityOrdered || 1,
          price: lineItem.price1 || lineItem.unitPrice,
          // Prefer AI extraction for work center flags, but JobBoss has final say
          needs_model: aiMatch?.needs_model || false,
          needs_paint: aiMatch?.needs_paint || false,
          needs_graphics: aiMatch?.needs_graphics ?? true,
          needs_opti: aiMatch?.needs_opti || false,
          // Flag as JobBoss matched
          jobBossMatch: true,
          source: aiMatch ? "merged" : "jobboss",
          lineNumber: lineItem.lineNumber,
        };
      });

      // Add any AI SKUs that weren't matched
      (extracted.skus || []).forEach((aiSku) => {
        const alreadyMerged = mergedSkus.some(
          (sku) =>
            sku.sku_name?.toLowerCase().includes(aiSku.sku_name?.toLowerCase()?.substring(0, 15)) ||
            aiSku.sku_name?.toLowerCase().includes(sku.sku_name?.toLowerCase()?.substring(0, 15))
        );

        if (!alreadyMerged) {
          mergedSkus.push({
            ...aiSku,
            jobBossMatch: false,
            source: "email",
          });
        }
      });
    }

    return {
      ...extracted,
      skus: mergedSkus,
      confidence: extracted.confidence || 0,
      jobBossLinked: !!jobBossData?.lineItems?.length,
    };
  }, [currentIntake, jobBossData]);

  // Save work order (draft) - doesn't push to production
  const handleSave = async (data) => {
    if (mode === "demo") {
      console.log("📝 [Demo] Saved work order:", data);
      alert(`Work order saved for ${data.project_name || data.quote_number}. (Demo mode - not actually saved to database)`);
      return;
    }

    // Live mode - save to database as draft
    try {
      const token = sessionStorage.getItem("msal.backendToken");
      // TODO: Add save endpoint that stores work order as draft without pushing to production
      console.log("📝 Saved work order:", data);
      alert(`Work order saved for ${data.project_name || data.quote_number}`);
    } catch (err) {
      console.error("Error saving:", err);
      alert("Error saving work order: " + err.message);
    }
  };

  // Complete & Push to Production
  const handleApprove = async (data) => {
    if (mode === "live" && currentIntake?.id && !currentIntake.id.startsWith("demo-")) {
      try {
        const token = sessionStorage.getItem("msal.backendToken");
        const res = await fetch(`/api/pm-center/work-orders/approve/${currentIntake.id}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reviewedData: data,
            skus: data.skus,
          }),
        });

        if (!res.ok) {
          throw new Error("Failed to complete work order");
        }

        const result = await res.json();
        console.log("✅ Work order pushed to production:", result);
        alert(`Work order ${result.project?.project_id || data.quote_number} pushed to production with ${data.skus?.length || 0} SKUs!`);
      } catch (err) {
        console.error("Error completing:", err);
        alert("Error completing work order: " + err.message);
        return;
      }
    } else {
      // Demo mode
      console.log("🚀 [Demo] Work order pushed to production:", data);
      alert(`[Demo] Work order ${data.quote_number || "?"} would be pushed to production with ${data.skus?.length || 0} SKUs!`);
    }

    setApprovedOrders((prev) => [...prev, { ...currentIntake, finalData: data }]);
    goToNext();
  };

  const handleSkip = async () => {
    if (mode === "live" && currentIntake?.id) {
      try {
        const token = sessionStorage.getItem("msal.backendToken");
        await fetch(`/api/pm-center/work-orders/skip/${currentIntake.id}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ reason: "Skipped during review" }),
        });
      } catch (err) {
        console.error("Error skipping:", err);
      }
    }

    setSkippedOrders((prev) => [...prev, currentIntake]);
    goToNext();
  };

  const handleRequestClarification = async (data) => {
    if (mode === "live" && currentIntake?.id) {
      try {
        const token = sessionStorage.getItem("msal.backendToken");
        await fetch(`/api/pm-center/work-orders/request-clarification/${currentIntake.id}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            questions: ["Need clarification on project details"],
            notes: `Clarification requested for ${data.project_name}`,
          }),
        });
      } catch (err) {
        console.error("Error requesting clarification:", err);
      }
    }

    alert(`Clarification requested for ${data.project_name}`);
    goToNext();
  };

  const goToNext = () => {
    if (currentIndex < intakes.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const resetReview = () => {
    setCurrentIndex(0);
    setApprovedOrders([]);
    setSkippedOrders([]);
    if (mode === "live") {
      fetchPendingWorkOrders();
    }
  };

  const allDone =
    intakes.length > 0 &&
    currentIndex >= intakes.length - 1 &&
    approvedOrders.length + skippedOrders.length >= intakes.length;

  return (
    <div className="min-h-screen bg-slate-100 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">PM Work Order Review</h1>
            <p className="text-gray-600">
              {mode === "demo"
                ? "Demo mode - Using sample data"
                : "Live mode - Reviewing real work orders"}
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm">
            {/* Mode Toggle */}
            <div className="flex rounded-lg overflow-hidden border border-gray-300">
              <button
                onClick={() => setMode("demo")}
                className={`px-3 py-1.5 ${
                  mode === "demo"
                    ? "bg-blue-600 text-gray-900"
                    : "bg-white text-gray-600 hover:bg-slate-200"
                }`}
              >
                Demo
              </button>
              <button
                onClick={() => setMode("live")}
                className={`px-3 py-1.5 ${
                  mode === "live"
                    ? "bg-blue-600 text-gray-900"
                    : "bg-white text-gray-600 hover:bg-slate-200"
                }`}
              >
                Live
              </button>
            </div>

            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full">
              {approvedOrders.length} Approved
            </span>
            <span className="px-3 py-1 bg-gray-200 text-gray-600 rounded-full">
              {skippedOrders.length} Skipped
            </span>
            {intakes.length > 0 && (
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                {currentIndex + 1} / {intakes.length}
              </span>
            )}
            <button
              onClick={resetReview}
              className="px-3 py-1 text-gray-600 hover:text-gray-800"
            >
              {mode === "live" ? "Refresh" : "Reset"}
            </button>
          </div>
        </div>

        {/* JobBoss Status */}
        {currentIntake && (
          <div className="mb-4 flex items-center gap-2 text-sm">
            {loadingJobBoss ? (
              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">
                Loading JobBoss data...
              </span>
            ) : jobBossData?.lineItems?.length > 0 ? (
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded flex items-center gap-1">
                <span className="text-green-600">✓</span>
                {jobBossData.count} SKUs from JobBoss Quote #{currentIntake.extractedData?.quote_number}
              </span>
            ) : currentIntake.extractedData?.quote_number ? (
              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">
                Quote #{currentIntake.extractedData.quote_number} not found in JobBoss
              </span>
            ) : (
              <span className="px-2 py-1 bg-slate-100 text-gray-600 rounded">
                No quote number - using AI extraction only
              </span>
            )}
          </div>
        )}

        {/* Progress Bar */}
        {intakes.length > 0 && (
          <div className="mb-6">
            <div className="flex gap-1">
              {intakes.map((_, i) => (
                <div
                  key={i}
                  className={`h-1 flex-1 rounded ${
                    i < currentIndex
                      ? "bg-green-500"
                      : i === currentIndex
                        ? "bg-blue-500"
                        : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <div className="text-4xl mb-4 animate-spin">⏳</div>
            <p className="text-gray-600">Loading pending work orders...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <div className="text-4xl mb-4">📭</div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">{error}</h2>
            <p className="text-gray-600 mb-6">
              {mode === "live"
                ? "No work orders are pending review. Check back later or switch to Demo mode."
                : "There was an error loading the data."}
            </p>
            <button
              onClick={() => (mode === "live" ? fetchPendingWorkOrders() : setMode("demo"))}
              className="px-6 py-3 bg-blue-600 text-gray-900 rounded-lg hover:bg-blue-700"
            >
              {mode === "live" ? "Retry" : "Try Demo Mode"}
            </button>
          </div>
        )}

        {/* Main Review UI */}
        {!loading && !error && intakes.length > 0 && !allDone && mergedData && (
          <PMWorkOrderReview
            intake={currentIntake}
            extractedData={mergedData}
            onApprove={handleApprove}
            onSave={handleSave}
            onSkip={handleSkip}
            onRequestClarification={handleRequestClarification}
            isDemo={mode === "demo"}
          />
        )}

        {/* All Done State */}
        {!loading && allDone && (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              All Work Orders Reviewed!
            </h2>
            <p className="text-gray-600 mb-6">
              {approvedOrders.length} approved, {skippedOrders.length} skipped
            </p>
            <button
              onClick={resetReview}
              className="px-6 py-3 bg-blue-600 text-gray-900 rounded-lg hover:bg-blue-700"
            >
              {mode === "live" ? "Check for More" : "Review Again"}
            </button>
          </div>
        )}

        {/* Speed Tips */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold text-gray-700 mb-3">Speed Tips for PMs</h3>
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
            <div className="flex items-start gap-2">
              <span className="text-green-600">⚡</span>
              <div>
                <strong>Quick Approve</strong>: If confidence is 90%+, use{" "}
                <kbd className="px-1 bg-slate-100 rounded">⌘+Enter</kbd> to approve
                instantly
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-blue-600">⌨️</span>
              <div>
                <strong>Keyboard Navigation</strong>: Tab through fields, Space to
                toggle work centers
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-purple-600">📦</span>
              <div>
                <strong>JobBoss Integration</strong>: When a quote is linked, SKUs
                come from JobBoss with accurate quantities
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-orange-600">✓</span>
              <div>
                <strong>Source Indicator</strong>: Green checkmarks mean the SKU
                data is from JobBoss quote
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
